import { FormEvent } from 'react';
import { FormControl } from 'react-bootstrap';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import SelectField from 'components/forms/selectField/SelectField';
import {
  ConditionType,
  TimeOperator,
  TimeType,
  TriggerEventType,
  TriggerTimeCondition,
  TriggerType,
} from 'models/automation/Triggers';
import { orderBy } from 'utils/arrayUtils';
import { buildCondition } from './TriggerAddEditModal.utils';
import { TriggerAddEditModalFormValues } from './TriggerAddEditModal.schema';
import TriggerAddEditModalTimeConditionExtraFields from './TriggerAddEditModalTimeConditionExtraFields';

const TriggerAddEditModalTimeConditionField = () => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<TriggerAddEditModalFormValues>();
  const triggerType = useWatch({ control, name: 'triggerType' });

  if (triggerType !== TriggerType.TIME_BASED) {
    return null;
  }

  const triggerTimeConditionOptions = Object.values(TriggerTimeCondition).map(
    (value) => ({
      label: t('pageAutomations.triggers.timeConditionType', {
        context: value,
      }),
      value,
    }),
  );

  const options = [
    { label: t('pageAutomations.triggers.timeConditionType'), value: '' },
    ...orderBy(triggerTimeConditionOptions, [{ field: 'label', order: 'asc' }]),
  ];

  const handleOnChange = (event: FormEvent<FormControl>) => {
    const { value } = event.target as HTMLSelectElement;

    if (value === '') {
      setValue('timeCondition.condition', buildCondition());
      setValue('eventType', '');
    } else if (value === TriggerTimeCondition.CHECK_IN) {
      setValue(
        'timeCondition.condition',
        buildCondition({
          backupTimeInMilliSeconds: 0,
          conditionType: ConditionType.BEFORE_STAY_STARTS,
          operator: TimeOperator.EQUAL,
          timeInMilliSeconds: 1,
          timeType: TimeType.DAYS,
        }),
        { shouldDirty: true },
      );
      setValue('eventType', TriggerEventType.BEFORE_STAY_STARTS);
    } else if (value === TriggerTimeCondition.CHECK_OUT) {
      setValue(
        'timeCondition.condition',
        buildCondition({
          conditionType: ConditionType.BEFORE_STAY_ENDS,
          operator: TimeOperator.EQUAL,
          timeInMilliSeconds: 1,
          timeType: TimeType.DAYS,
        }),
        { shouldDirty: true },
      );
      setValue('eventType', TriggerEventType.BEFORE_STAY_ENDS);
    } else if (value === TriggerTimeCondition.DAY_OF_WEEK) {
      setValue(
        'timeCondition.condition',
        buildCondition({
          conditionType: ConditionType.DAY_OF_WEEK,
          daysOfWeek: [],
        }),
        { shouldDirty: true },
      );
      setValue('eventType', TriggerEventType.DAY_OF_WEEK);
    }
  };

  return (
    <>
      <SelectField
        colSmInput={12}
        label={t('pageAutomations.triggers.modal.timeConditionType')}
        labelOnTop
        name="timeCondition.type"
        onChangeHandler={handleOnChange}
        options={options}
        required
      />
      <TriggerAddEditModalTimeConditionExtraFields />
    </>
  );
};

export default TriggerAddEditModalTimeConditionField;
